"use client";

import { useEffect, useMemo, useState } from "react";
import { Button } from "@mui/material";
import PersonOutlinedIcon from "@mui/icons-material/PersonOutlined";
import LocalPhoneOutlinedIcon from "@mui/icons-material/LocalPhoneOutlined";
import LocationOnOutlinedIcon from "@mui/icons-material/LocationOnOutlined";
import HomeOutlinedIcon from "@mui/icons-material/HomeOutlined";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { useDpStore } from "@/store/dpStore";
import GetInTouchHeader from "./GetInTouchHeader";
import GetInTouchField from "./GetInTouchField";
import WorkTypeSelector from "./WorkTypeSelector";
import { WORK_TYPES } from "./_constants/workTypes";
import { useEnquiryFlowStore } from "@/store/enquiryFlowStore";
import { useSurveySheetStore } from "@/store/surveySheetStore";
import { navigateBack } from "./enquiryFlowHistory";
import { inhomzEvents } from "@/app/(fevicol-inhomz)/_lib/events";
import { profileEventProps } from "@/app/(fevicol-inhomz)/_lib/profileEventProps";
import { useInhomzTrack } from "@/app/(fevicol-inhomz)/_hooks/useInhomzTrack";
import { useTrackWhenActive } from "@/app/(fevicol-inhomz)/_hooks/useTrackWhenActive";
import { getLastEnquiry, saveLastEnquiry } from "@/lib/utils/last-enquiry-storage";
import { getWhatsAppNumberError, restrictToTenDigits, validateWhatsAppNumber } from "@/lib/utils/connect-form-validation";

// Step 1 (content only, no sheet wrapper) of the enquiry sidesheet chain —
// hosted inline by EnquiryFlowShell, which slides between this, the survey
// step, and Review. Relies on the MapModal instance that's already mounted
// alongside ConnectButton on the digital-profile page (via the shared
// dpStore isMapModalOpen flag) rather than mounting its own.
export default function MandatoryFormSheet() {
  const {
    activeStep,
    digitalProfile,
    mandatoryData,
    surveyAnswers,
    isEditingFromReview,
    backToMandatory,
    goToReview
  } = useEnquiryFlowStore();
  const surveySheet = useSurveySheetStore();
  const {
    enquiryFormData,
    setEnquiryFormData,
    setIsMapModalOpen
  } = useDpStore();
  const isOpen = activeStep === "mandatory";
  const track = useInhomzTrack();
  // The survey slides over this step while activeStep stays "mandatory".
  const surveyInFlow = surveySheet.isOpen && surveySheet.context === "enquiryFlow";
  useTrackWhenActive(isOpen && !surveyInFlow, inhomzEvents.clientForm.VIEWED, profileEventProps(digitalProfile));
  const [workTypes, setWorkTypes] = useState([]);
  const [fullName, setFullName] = useState("");
  const [contactNumber, setContactNumber] = useState("");
  const [contactNumberTouched, setContactNumberTouched] = useState(false);
  const [flatNumber, setFlatNumber] = useState("");
  useEffect(() => {
    if (!isOpen || !digitalProfile) return;
    if (mandatoryData) {
      // Re-opened via the Review sheet's edit pencil — restore the draft.
      setWorkTypes(mandatoryData.workTypes || []);
      setFullName(mandatoryData.fullName || "");
      setContactNumber(mandatoryData.contactNumber || "");
      setContactNumberTouched(false);
      setFlatNumber(mandatoryData.flatNumber || "");
      return;
    }
    const last = getLastEnquiry();
    if (last) {
      setWorkTypes(last.workTypes || []);
      setFullName(last.fullName || "");
      setContactNumber(last.contactNumber || "");
      setContactNumberTouched(false);
      setFlatNumber(last.flatNumber || "");
      if (last.siteLocation) {
        setEnquiryFormData({
          ...enquiryFormData,
          siteLocation: last.siteLocation,
          siteLocationDetails: last.siteLocationDetails || null
        });
      }
    } else {
      setWorkTypes([]);
      setFullName("");
      setContactNumber("");
      setContactNumberTouched(false);
      setFlatNumber("");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, digitalProfile?.contractorId]);
  const siteLocation = enquiryFormData.siteLocation || "";
  const primaryLocation = siteLocation.split(",")[0];
  const toggleWorkType = value => {
    setWorkTypes(prev => prev.includes(value) ? prev.filter(item => item !== value) : [...prev, value]);
  };
  const contactNumberError = contactNumberTouched ? getWhatsAppNumberError(contactNumber) : null;
  const isFormValid = useMemo(() => workTypes.length > 0 && fullName.trim() !== "" && validateWhatsAppNumber(contactNumber) && siteLocation.trim() !== "", [workTypes, fullName, contactNumber, siteLocation]);
  const handleBack = () => {
    track(inhomzEvents.clientForm.BACK_CLICKED, profileEventProps(digitalProfile));
    navigateBack();
  };
  const handleContinue = () => {
    track(inhomzEvents.clientForm.CONTINUE_CLICKED, {
      ...profileEventProps(digitalProfile),
      work_types: workTypes.join(", "),
      is_editing: isEditingFromReview
    });
    const collectedMandatoryData = {
      workTypes,
      fullName,
      contactNumber,
      flatNumber,
      siteLocation,
      siteLocationDetails: enquiryFormData.siteLocationDetails || null
    };
    // Persisted here, not only on submit, so a user who leaves mid-survey skips this form next time.
    saveLastEnquiry(collectedMandatoryData);
    if (isEditingFromReview) {
      goToReview({
        mandatoryData: collectedMandatoryData
      });
      return;
    }
    surveySheet.open({
      initialAnswers: surveyAnswers && Object.keys(surveyAnswers).length > 0 ? surveyAnswers : undefined,
      onComplete: answers => goToReview({
        mandatoryData: collectedMandatoryData,
        surveyAnswers: answers
      }),
      onSkip: answers => goToReview({
        mandatoryData: collectedMandatoryData,
        surveyAnswers: answers
      }),
      onBack: () => backToMandatory(collectedMandatoryData),
      context: "enquiryFlow"
    });
  };
  const contractorFirstName = (digitalProfile?.profileName || "Contractor").split(" ")[0];
  return <div className="mx-auto flex h-full w-full flex-col bg-[--colorSurfaceSurface]">
      <GetInTouchHeader title={`Connect with ${contractorFirstName}`} onBack={handleBack} />

      <div className="flex-1 space-y-6 overflow-y-auto p-4">
        <WorkTypeSelector options={WORK_TYPES} selected={workTypes} onToggle={toggleWorkType} />

        <GetInTouchField label="Your name" name="fullName" value={fullName} onChange={e => setFullName(e.target.value)} icon={PersonOutlinedIcon} />

        <GetInTouchField label="Your phone number" name="contactNumber" value={contactNumber} onChange={e => setContactNumber(restrictToTenDigits(e.target.value))} onBlur={() => setContactNumberTouched(true)} error={Boolean(contactNumberError)} errorMessage={contactNumberError} icon={LocalPhoneOutlinedIcon} />

        <GetInTouchField label="Your area (e.g. Whitefield)" name="siteLocation" value={siteLocation} valueTitle={primaryLocation} onFieldClick={() => setIsMapModalOpen(true)} readOnly icon={LocationOnOutlinedIcon} multiline placeholder="Your area (e.g. Whitefield)" />

        <GetInTouchField label="Flat / House / Apartment No." name="flatNumber" value={flatNumber} onChange={e => setFlatNumber(e.target.value)} icon={HomeOutlinedIcon} />

        <p className="m3-body-small pb-6 text-[--textDisabledDisabled]">
          Your contact number and address will be shared only with the Interior Expert you choose
        </p>
      </div>

      <div className="sticky bottom-0 flex items-center gap-3 border-t border-[--colorSecondarySecondaryContainer] bg-[--colorSurfaceSurface] px-6 py-4">
        <div className="flex flex-1 items-start gap-1.5">
          <InfoOutlinedIcon sx={{
          fontSize: "16px"
        }} className="mt-0.5 shrink-0 text-[--textDisabledDisabled]" />
          <p className="m3-body-small text-[--textDisabledDisabled] italic">
            Note: Interior Experts on this platform do not take on repair work.
          </p>
        </div>

        <Button type="button" variant={isFormValid ? "contained" : "outlined"} disabled={!isFormValid} onClick={handleContinue} className="m3-label-large" sx={{
        width: "120px",
        height: "40px",
        borderRadius: "100px",
        flexShrink: 0,
        textTransform: "none",
        ...(isFormValid ? {
          backgroundColor: "var(--colorPrimaryPrimary, #004BEE)",
          color: "var(--textPrimaryOnColorPrimary, #F2F1E9)",
          "&:hover": {
            backgroundColor: "var(--colorPrimaryPrimary, #004BEE)",
            opacity: 0.9
          }
        } : {
          border: "1px solid var(--colorDisabledDisabled, #17191C59)",
          color: "var(--textDisabledDisabled, #17191C59)"
        }),
        "&.Mui-disabled": {
          border: "1px solid var(--colorDisabledDisabled, #17191C59)",
          color: "var(--textDisabledDisabled, #17191C59)",
          backgroundColor: "transparent"
        }
      }}>
          Continue
        </Button>
      </div>
    </div>;
}